import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Divider } from '@mui/material';

import CnaAffected from 'pages/item/v2/components/CnaAffected';
import CpeConfigurations from 'pages/item/v2/components/CpeConfigurations';
import Breadcrumbs from 'pages/Search/components/Breadcrumbs';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';

import Affected from './Affected';
import Code from './Code';
import Description from './Description';
import HackApp from './HackApp';
import References from './References';
import Score from './Score';
import SocialReferences from './SocialReferences';
import Summary from './Summary';
import Tags from './Tags';
import Title from './Title';

const CUSTOM_TYPES = { hackapp: HackApp };

const useStyles = makeStyles({ name: 'Content' })({
    breadcrumbs: {
        marginTop: 40,
        marginBottom: 16
    },
    title: {
        marginBottom: 24
    },
    summary: {
        marginBottom: 12
    },
    score: {
        display: 'none',
        transition: 'all 0.2s ease-out',
        paddingBottom: 0,
        '@media (max-width: 960px)': {
            display: 'block',
            paddingBottom: 60
        }
    }
});

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
}

const Content: React.FC<Props> = ({ data }) => {
    const {
        id,
        title,
        type,
        description,
        sourceData,
        privateArea,
        cpe,
        cpeConfigurations,
        affectedSoftware,
        affectedPackage,
        cnaAffected,
        references,
        vulnersCpeConfiguration,
        metrics,
        cna_cvss,
        reporter,
        href,
        published,
        enchantments: { twitter, reddit, vulners_cpe_configuration }
    } = data;
    const { classes } = useStyles();

    const aiScore = data?.enchantments?.score;
    const epss = data?.epss;
    const posts = useMemo(() => [...(twitter?.tweets ?? []), ...(reddit?.posts ?? [])], [
        reddit?.posts,
        twitter?.tweets
    ]);

    const breadcrumbs = useMemo(
        () => [
            { label: 'Vulners', link: ROUTES.ROOT() },
            { label: normalizeName(type), link: ROUTES.TYPE({ typeName: type, page: null, order: null }) },
            { label: title || id, link: '', disabled: true }
        ],
        [id, title, type]
    );

    return (
        <>
            <Summary
                id={id}
                reporter={reporter}
                href={href}
                type={type}
                published={published}
                className={classes.summary}
            />
            <Divider />
            <Breadcrumbs items={breadcrumbs} className={classes.breadcrumbs} />
            <Title title={title || id} className={classes.title} />
            <Tags data={data} />
            <div className={classes.score}>
                <Score aiScore={aiScore} metrics={metrics} epss={epss} cnaCvss={cna_cvss} id={id} />
            </div>
            <Description privateArea={privateArea} description={description} />
            {CUSTOM_TYPES[type] ? React.createElement(CUSTOM_TYPES[type], { data }) : null}
            <Code sourceData={sourceData} />
            <CpeConfigurations cpeConfigurations={cpeConfigurations} />
            <Affected
                cpe={cpe}
                affectedPackage={affectedPackage}
                affectedSoftware={affectedSoftware}
                vulnersCpe={vulnersCpeConfiguration || vulners_cpe_configuration}
            />
            <CnaAffected cnaAffected={cnaAffected} />
            <References references={references} />
            <SocialReferences posts={posts} />
        </>
    );
};

export default Content;
